import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bringIntoBookApi } from '../../api/bringIntoBook';
import { bringIntoBookPath } from '../../utils/bringIntoBookPath';
import {
  BRING_SERIES_PEOPLE_LABEL,
  BRING_SERIES_PEOPLE_LEDE,
  BRING_SERIES_PEOPLE_LINK,
  BRING_SERIES_PEOPLE_NOT_NOW,
} from '../../utils/writerCopy';
import './BringSeriesPeopleOffer.css';

const dismissKey = (storyId) => `brainstormy.bring-series-people.not-now.${storyId}`;

function readDismissed(storyId) {
  try {
    return window.sessionStorage.getItem(dismissKey(storyId)) === '1';
  } catch (err) {
    return false;
  }
}

function writeDismissed(storyId) {
  try {
    window.sessionStorage.setItem(dismissKey(storyId), '1');
  } catch (err) {
    // Private mode: the offer may come back this visit. Nothing is written.
  }
}

// What Bring into this book can carry (#900). Theme is a series idea, not a book note.
const BRINGABLE_KINDS = new Set(['people', 'world', 'custom']);

/** True when the series still has notes this book has not brought in (run 21: a saved
 * Custom note, the series bible, was never offered because only people counted). */
export function hasSeriesPeopleToBring(listing) {
  const offers = Array.isArray(listing?.offers) ? listing.offers : [];
  return offers.some((offer) => BRINGABLE_KINDS.has(offer?.kind) && !offer.already_accepted);
}

/**
 * Cold-start run 11: points a series book at Bring into this book (#900)
 * when the series has notes this book has not brought in. Free GET, writes
 * nothing. Only a known series book is asked (#1473). Standalone books,
 * and books whose series status is still unknown, render nothing and
 * make no request. Books with nothing left to bring render nothing.
 *
 * variant="card": a dismissible offer (the new book's Discovery talk).
 * variant="link": one line beside an empty-people message.
 */
export default function BringSeriesPeopleOffer({
  storyId,
  isSeries,
  variant = 'link',
  api = bringIntoBookApi,
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShow(false);
    if (!storyId || isSeries !== true) return undefined;
    if (variant === 'card' && readDismissed(storyId)) return undefined;
    api.list(storyId)
      .then((listing) => {
        if (!cancelled) setShow(hasSeriesPeopleToBring(listing));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [storyId, isSeries, variant, api]);

  if (!show) return null;

  const link = (
    <Link className="bring-series-people__link" to={bringIntoBookPath(storyId)}>
      {BRING_SERIES_PEOPLE_LINK}
    </Link>
  );

  if (variant !== 'card') {
    return (
      <p className="bring-series-people bring-series-people--line" data-testid="bring-series-people">
        {link}
      </p>
    );
  }

  return (
    <section
      className="bring-series-people bring-series-people--card"
      aria-labelledby="bring-series-people-heading"
      data-testid="bring-series-people"
    >
      <h3 id="bring-series-people-heading" className="bring-series-people__label">
        {BRING_SERIES_PEOPLE_LABEL}
      </h3>
      <p className="bring-series-people__lede">{BRING_SERIES_PEOPLE_LEDE}</p>
      <div className="bring-series-people__actions">
        {link}
        <button
          type="button"
          className="bring-series-people__dismiss"
          onClick={() => {
            writeDismissed(storyId);
            setShow(false);
          }}
        >
          {BRING_SERIES_PEOPLE_NOT_NOW}
        </button>
      </div>
    </section>
  );
}
